import { Link, Navigate } from "react-router-dom";
import { Check, ChevronRight, ArrowRight, Phone } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { CTAButton } from "@/components/CTAButton";
import { Seo } from "@/components/Seo";
import { useAudit } from "@/components/AuditContext";
import { METIERS } from "@/lib/metiersData";
import { LOCAL_PAGES } from "@/lib/localData";
import { INCLUDED, NETWORK_NOTE, COMPANY } from "@/lib/content";

const Breadcrumb = ({ items }) => (
  <nav aria-label="Fil d'Ariane" className="flex items-center flex-wrap gap-2 text-sm text-white/60">
    {items.map((it, i) => (
      <span key={it.path} className="flex items-center gap-2">
        {i > 0 && <ChevronRight className="w-3.5 h-3.5" />}
        {i < items.length - 1 ? (
          <Link to={it.path} className="hover:text-white transition-colors">{it.name}</Link>
        ) : (
          <span className="text-white">{it.name}</span>
        )}
      </span>
    ))}
  </nav>
);

export default function MetierPage({ slug }) {
  const m = METIERS[slug] || LOCAL_PAGES[slug];
  const { openAudit } = useAudit();

  if (!m) return <Navigate to="/" replace />;

  const path = `/${slug}`;
  const breadcrumb = [
    { name: "Accueil", path: "/" },
    { name: m.navLabel, path },
  ];

  return (
    <>
      <Seo title={m.seoTitle} description={m.seoDescription} path={path} />

      <section className="bg-navy grain relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="pointer-events-none absolute -top-24 right-[-8%] w-[40rem] h-[40rem] rounded-full bg-amber-500/10 blur-[140px]" aria-hidden />
        <div className="relative z-10 mx-auto max-w-5xl px-5 sm:px-8">
          <Breadcrumb items={breadcrumb} />
          <Reveal>
            <span className="mt-8 inline-block text-xs uppercase tracking-[0.25em] font-medium text-amber-500">{m.kicker}</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter text-white max-w-4xl text-balance">{m.seoTitle}</h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 font-display text-2xl md:text-3xl text-amber-400 max-w-3xl text-balance">{m.title}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">{m.intro}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              {m.phoneCta && (
                <a href={`tel:${COMPANY.phoneHref}`} data-testid="metier-hero-phone" className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-600 text-white px-8 py-4 text-base font-medium hover:bg-amber-700 transition-colors">
                  <Phone className="w-4 h-4" /> {COMPANY.phone}
                </a>
              )}
              <CTAButton size="lg" withArrow onClick={openAudit} data-testid="metier-hero-cta">
                Faire le point en 30 minutes
              </CTAButton>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 space-y-20">
          {m.sections.map((s, i) => (
            <Reveal key={i}>
              <div className="grid md:grid-cols-12 gap-6 md:gap-10">
                <div className="md:col-span-4">
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-navy">{s.heading}</h2>
                </div>
                <div className="md:col-span-8">
                  {s.paragraphs && (
                    <div className="space-y-4 text-lg text-slate-600 leading-relaxed">
                      {s.paragraphs.map((p, j) => <p key={j}>{p}</p>)}
                    </div>
                  )}
                  {s.links && (
                    <ul className="flex flex-wrap gap-3">
                      {s.links.map((l) => (
                        <li key={l.to}>
                          <Link to={l.to} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-navy font-medium hover:border-amber-500 hover:text-amber-700 transition-colors">
                            {l.label} <ArrowRight className="w-4 h-4" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.blocks && (
                    <div className="space-y-8">
                      {s.blocks.map((b) => (
                        <div key={b.title} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-amber-600 mt-1.5 shrink-0" />
                          <div>
                            <h3 className="font-display text-xl font-semibold text-navy">{b.title}</h3>
                            <p className="mt-2 text-lg text-slate-600 leading-relaxed">{b.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Rappel : tout est compris dans l'offre, tarifs sur /offres */}
      <section className="bg-[#f8fafc] py-20 md:py-24 border-y border-slate-200">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-4 py-1.5">
              <Check className="w-4 h-4 text-amber-600" />
              <span className="text-amber-700 text-sm font-medium">Tout compris, un seul prix, une seule facture</span>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy max-w-3xl text-balance">
              Pas de catalogue de licences. Toute votre informatique, prise en charge.
            </h2>
          </Reveal>
          <Stagger className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-4">
            {INCLUDED.map((it) => (
              <StaggerItem key={it} className="flex items-start gap-3 text-slate-700">
                <Check className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" /><span>{it}</span>
              </StaggerItem>
            ))}
          </Stagger>
          <p className="mt-8 text-slate-500 text-sm leading-relaxed max-w-3xl">{NETWORK_NOTE}</p>
          <Reveal delay={0.1}>
            <Link to="/offres" className="mt-10 inline-flex items-center gap-2 text-navy font-medium link-underline">
              Voir la formule et les tarifs <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {m.faq && m.faq.length > 0 && (
        <section className="bg-white py-20 md:py-28" data-testid="metier-faq">
          <div className="mx-auto max-w-3xl px-5 sm:px-8">
            <Reveal>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy">Questions fréquentes</h2>
            </Reveal>
            <Reveal delay={0.05}>
              <Accordion type="single" collapsible className="mt-10">
                {m.faq.map((f, i) => (
                  <AccordionItem key={f.q} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left font-display text-lg text-navy">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-base text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              {m.legalSource && (
                <p className="mt-6 text-sm text-slate-500">
                  <a href={m.legalSource.url} target="_blank" rel="noopener noreferrer" className="underline hover:text-navy">
                    {m.legalSource.label}
                  </a>
                </p>
              )}
            </Reveal>
          </div>
        </section>
      )}

      <section className="bg-navy grain relative py-24 md:py-28 overflow-hidden">
        <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50rem] h-[28rem] rounded-full bg-amber-500/12 blur-[140px]" aria-hidden />
        <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white text-balance">
              Faisons le point sur votre informatique en 30 minutes.
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              Sauvegardes, mises à jour, sécurité, délai d'intervention en cas de panne : vous repartez avec 3 à 5 constats concrets, que vous travailliez ensuite avec nous ou non.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <CTAButton size="lg" withArrow className="mt-10" onClick={openAudit} data-testid="metier-audit-cta">
              Réserver un échange gratuit
            </CTAButton>
          </Reveal>
        </div>
      </section>
    </>
  );
}
